import { useId, useMemo, useState } from "react";

import { reportingViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { parseTicketNotes } from "../app/ticketNotes";
import type { Customer, Expense, ExpenseVendor, ProductPlan, Ticket } from "../app/types";
import { getTicketSlaDeadlineTimestamp } from "../app/utils";

type TicketMetric = "opened" | "closed" | "openedClosed" | "firstResponse";
type ProductMetric = "customers" | "monthlySpend" | "seats" | "customerSpend";
type ExpenseMetric = "byVendor" | "byMonth" | "byCustomer";
type ProductChartMode = "snapshot" | "timeline";
type PrimaryReportView = "tickets" | "products" | "expenses";
type ChartStyle = "line" | "bar";
type ValueUnit = "count" | "hours" | "currency";

type SimplifiedReportingViewProps = {
  customers: Customer[];
  currency: string;
  expenses: Expense[];
  vendors: ExpenseVendor[];
  isLoadingReporting?: boolean;
  language: AppLanguage;
  productPlans: ProductPlan[];
  tickets: Ticket[];
  canViewAllExpenses?: boolean;
  members?: { email: string; name: string }[];
};

type ChartSeries = {
  color: string;
  label: string;
  values: number[];
};

type ChartData = {
  labels: string[];
  series: ChartSeries[];
  style: ChartStyle;
  unit: ValueUnit;
};

type SummaryCardProps = {
  label: string;
  value: string;
  detail: string;
  isLoading?: boolean;
};

const chartColors = ["#00855d", "#175cd3", "#f79009", "#7a5af8"];
const currencyOptions = [
  { label: "USD ($)", code: "USD" },
  { label: "EUR (€)", code: "EUR" },
  { label: "GBP (£)", code: "GBP" },
  { label: "CAD (CA$)", code: "CAD" },
  { label: "JPY (¥)", code: "JPY" },
  { label: "AUD (A$)", code: "AUD" },
  { label: "NZD (NZ$)", code: "NZD" },
  { label: "SGD (S$)", code: "SGD" },
] as const;
type ReportingViewCopy = typeof reportingViewCopy.en;

function getCurrencyCode(selection: string) {
  return currencyOptions.find(option => option.label === selection)?.code ?? currencyOptions[0].code;
}

function parseTimestamp(value: string | null | undefined) {
  if (!value) {
    return Number.NaN;
  }

  return new Date(value).getTime();
}

function toDateInputValue(timestamp: number) {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function parseDateInput(value: string, endOfDay = false) {
  if (!value) {
    return Number.NaN;
  }

  const timestamp = new Date(`${value}T00:00:00`).getTime();

  if (Number.isNaN(timestamp)) {
    return Number.NaN;
  }

  return endOfDay ? timestamp + 86_399_999 : timestamp;
}

function formatCompactNumber(value: number, language: AppLanguage) {
  return new Intl.NumberFormat(getIntlLocale(language), {
    maximumFractionDigits: Math.abs(value) >= 1_000 ? 1 : 0,
    notation: Math.abs(value) >= 1_000 ? "compact" : "standard",
  }).format(value);
}

function formatCurrency(value: number, language: AppLanguage, currencySelection: string) {
  return new Intl.NumberFormat(getIntlLocale(language), {
    currency: getCurrencyCode(currencySelection),
    maximumFractionDigits: value >= 10_000 ? 0 : 2,
    notation: value >= 10_000 ? "compact" : "standard",
    style: "currency",
  }).format(value);
}

function formatHours(value: number, language: AppLanguage) {
  return language === "ja" ? `${value.toFixed(1)}時間` : `${value.toFixed(1)}h`;
}

function formatExpenseMonth(monthKey: string, language: AppLanguage) {
  const parts = monthKey.split("-");
  const year = Number.parseInt(parts[0] ?? "", 10);
  const month = Number.parseInt(parts[1] ?? "", 10);

  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return monthKey;
  }

  return new Intl.DateTimeFormat(getIntlLocale(language), { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year, Math.max(0, month - 1), 1)));
}

function formatAxisValue(value: number, unit: ValueUnit, language: AppLanguage, currencySelection: string) {
  switch (unit) {
    case "currency":
      return new Intl.NumberFormat(getIntlLocale(language), {
        currency: getCurrencyCode(currencySelection),
        maximumFractionDigits: 0,
        notation: Math.abs(value) >= 1_000 ? "compact" : "standard",
        style: "currency",
      }).format(value);
    case "hours":
      return language === "ja" ? `${value.toFixed(1)}時間` : `${value.toFixed(1)}h`;
    case "count":
      return formatCompactNumber(value, language);
  }
}

function getNiceStep(roughStep: number) {
  if (!Number.isFinite(roughStep) || roughStep <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  if (normalized <= 1) {
    return magnitude;
  }

  if (normalized <= 2) {
    return 2 * magnitude;
  }

  if (normalized <= 5) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
}

function getChartAxis(allValues: number[], unit: ValueUnit) {
  const safeValues = allValues.length > 0 ? allValues : [0];
  const rawMax = Math.max(...safeValues, 0);
  const rawMin = unit === "count" || unit === "currency" ? 0 : Math.min(...safeValues, 0);
  const targetTickCount = 4;
  const roughStep = (rawMax - rawMin) / targetTickCount;
  const step = unit === "count"
    ? Math.max(1, Math.round(roughStep) || 1)
    : getNiceStep(roughStep || (rawMax === 0 ? 1 : rawMax / targetTickCount));
  const min = unit === "count" || unit === "currency"
    ? 0
    : Math.floor(rawMin / step) * step;
  const max = Math.max(step, Math.ceil(rawMax / step) * step);
  const ticks: number[] = [];

  for (let value = max; value >= min; value -= step) {
    ticks.push(Number(value.toFixed(4)));
  }

  if (ticks[ticks.length - 1] !== min) {
    ticks.push(min);
  }

  return { max, min, ticks };
}

function truncateAxisLabelPart(value: string, maxCharacters: number) {
  if (value.length <= maxCharacters) {
    return value;
  }

  return `${value.slice(0, Math.max(maxCharacters - 1, 1)).trimEnd()}…`;
}

function getAxisLabelLines(label: string, maxLines: number, maxCharactersPerLine: number) {
  const segments = label
    .split(" • ")
    .map(segment => segment.trim())
    .filter(Boolean);

  if (segments.length === 0) {
    return [truncateAxisLabelPart(label, maxCharactersPerLine)];
  }

  const lines = segments.slice(0, maxLines).map(segment => truncateAxisLabelPart(segment, maxCharactersPerLine));

  if (segments.length > maxLines) {
    const lastLine = lines[lines.length - 1] ?? "";
    lines[lines.length - 1] = truncateAxisLabelPart(`${lastLine} +${segments.length - maxLines}`, maxCharactersPerLine);
  }

  return lines;
}

function getChartPoint(value: number, index: number, length: number, width: number, height: number, minValue: number, maxValue: number) {
  const x = length === 1 ? width / 2 : (width / Math.max(length - 1, 1)) * index;
  const y = height - ((value - minValue) / Math.max(maxValue - minValue, 1)) * height;

  return { x, y };
}

function buildSmoothLinePath(values: number[], width: number, height: number, minValue: number, maxValue: number) {
  if (values.length === 0) {
    return "";
  }

  const points = values.map((value, index) => getChartPoint(value, index, values.length, width, height, minValue, maxValue));

  if (points.length === 1) {
    const point = points[0]!;
    return `M${point.x} ${point.y}`;
  }

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M${point.x} ${point.y}`;
    }

    const previous = points[index - 1]!;
    const controlDistance = (point.x - previous.x) * 0.5;
    return `${path} C${previous.x + controlDistance} ${previous.y}, ${point.x - controlDistance} ${point.y}, ${point.x} ${point.y}`;
  }, "");
}

function buildAreaPath(values: number[], width: number, height: number, minValue: number, maxValue: number) {
  if (values.length === 0) {
    return "";
  }

  const firstX = values.length === 1 ? width / 2 : 0;
  const lastX = values.length === 1 ? width / 2 : width;
  const line = buildSmoothLinePath(values, width, height, minValue, maxValue);

  return `${line} L ${lastX} ${height} L ${firstX} ${height} Z`;
}

function shouldRenderXAxisLabel(index: number, total: number, chartStyle: ChartStyle) {
  if (chartStyle === "bar" || total <= 9 || index === 0 || index === total - 1) {
    return true;
  }

  return index % Math.ceil(total / 6) === 0;
}

function getCustomerStartTimestamp(customer: Customer) {
  const noteTimestamps = customer.notes.map(note => parseTimestamp(note.createdAt)).filter(timestamp => Number.isFinite(timestamp));
  const historyTimestamps = customer.actionHistory.map(entry => parseTimestamp(entry.createdAt)).filter(timestamp => Number.isFinite(timestamp));
  const fallbackRenewalTimestamp = parseTimestamp(customer.renewalDate);
  const allTimestamps = [...noteTimestamps, ...historyTimestamps];

  if (allTimestamps.length > 0) {
    return Math.min(...allTimestamps);
  }

  return Number.isFinite(fallbackRenewalTimestamp) ? fallbackRenewalTimestamp : Date.now();
}

function getDateRangeBounds(tickets: Ticket[], customers: Customer[]) {
  void tickets;
  void customers;
  const now = Date.now();

  return {
    end: toDateInputValue(now),
    start: toDateInputValue(now - 30 * 86_400_000),
  };
}

function getProductPlanLookup(productPlans: ProductPlan[]) {
  const byId = new Map(productPlans.map(plan => [plan.id, plan]));
  const byName = new Map(productPlans.map(plan => [plan.name.trim().toLowerCase(), plan]));

  return { byId, byName };
}

function getMatchingProductPlan(product: Customer["products"][number], lookup: ReturnType<typeof getProductPlanLookup>) {
  return (product.productPlanId ? lookup.byId.get(product.productPlanId) : null)
    ?? lookup.byName.get(product.name.trim().toLowerCase())
    ?? null;
}

function getNormalizedMonthlyAmount(interval: ProductPlan["price"]["interval"], amount: number) {
  switch (interval) {
    case "Daily":
      return amount * 30;
    case "Weekly":
      return amount * 4.33;
    case "Monthly":
      return amount;
    case "Yearly":
      return amount / 12;
    case "One-time":
      return 0;
  }
}

function getEstimatedCustomerMonthlySpend(customer: Customer, productPlans: ProductPlan[]) {
  const lookup = getProductPlanLookup(productPlans);

  return customer.products.reduce((total, product) => {
    const matchingPlan = getMatchingProductPlan(product, lookup);

    if (!matchingPlan) {
      return total;
    }

    const amount = Number.parseFloat(matchingPlan.price.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      return total;
    }

    return total + getNormalizedMonthlyAmount(matchingPlan.price.interval, amount) * Math.max(product.seats, 1);
  }, 0);
}

function getEstimatedProductMonthlySpend(product: Customer["products"][number], lookup: ReturnType<typeof getProductPlanLookup>) {
  const matchingPlan = getMatchingProductPlan(product, lookup);

  if (!matchingPlan) {
    return 0;
  }

  const amount = Number.parseFloat(matchingPlan.price.amount);

  if (!Number.isFinite(amount) || amount <= 0) {
    return 0;
  }

  return getNormalizedMonthlyAmount(matchingPlan.price.interval, amount) * Math.max(product.seats, 1);
}

function getCustomerProductStartTimestamp(customer: Customer, product: Customer["products"][number]) {
  const normalizedProductName = product.name.trim().toLowerCase();
  const matchingHistoryTimestamps = customer.actionHistory
    .filter(entry => entry.entity === "Product")
    .map(entry => ({
      text: `${entry.action} ${entry.detail}`.toLowerCase(),
      timestamp: parseTimestamp(entry.createdAt),
    }))
    .filter(entry => entry.text.includes(normalizedProductName) && Number.isFinite(entry.timestamp))
    .map(entry => entry.timestamp);

  if (matchingHistoryTimestamps.length > 0) {
    return Math.min(...matchingHistoryTimestamps);
  }

  const customerStartTimestamp = getCustomerStartTimestamp(customer);

  if (Number.isFinite(customerStartTimestamp)) {
    return customerStartTimestamp;
  }

  const renewalTimestamp = parseTimestamp(product.renewalDate);
  return Number.isFinite(renewalTimestamp) ? renewalTimestamp : Date.now();
}

function isSlaBroken(ticket: Ticket, referenceTime: number) {
  const deadline = getTicketSlaDeadlineTimestamp(ticket.createdAt, ticket.sla, referenceTime, ticket.slaPausedAt, ticket.slaPausedTotalMs);

  if (deadline === null) {
    return false;
  }

  const comparisonTime = ticket.status === "Resolved"
    ? parseTimestamp(ticket.lastUpdate)
    : referenceTime;

  return Number.isFinite(comparisonTime) && comparisonTime > deadline;
}

function getFirstResponseHours(ticket: Ticket) {
  const createdAt = parseTimestamp(ticket.createdAt);
  const firstResponseAt = Math.min(
    ...parseTicketNotes(ticket.notes)
      .filter(note => note.kind === "external" && note.deliveryStatus !== "received" && note.deliveryStatus !== "failed")
      .map(note => parseTimestamp(note.createdAt))
      .filter(timestamp => Number.isFinite(timestamp)),
  );

  if (!Number.isFinite(createdAt) || !Number.isFinite(firstResponseAt) || firstResponseAt < createdAt) {
    return null;
  }

  return (firstResponseAt - createdAt) / 3_600_000;
}

function createDailyBuckets(startTimestamp: number, endTimestamp: number, language: AppLanguage) {
  const buckets: Array<{ label: string; start: number; end: number }> = [];

  for (let current = startTimestamp; current <= endTimestamp; current += 86_400_000) {
    const label = new Intl.DateTimeFormat(getIntlLocale(language), { month: "short", day: "numeric" }).format(new Date(current));
    buckets.push({ label, start: current, end: current + 86_399_999 });
  }

  return buckets;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatListPreview(values: string[], maxVisible = 2) {
  if (values.length <= maxVisible) {
    return values.join(", ");
  }

  return `${values.slice(0, maxVisible).join(", ")} +${values.length - maxVisible}`;
}

function escapeCsvValue(value: string | number) {
  const normalized = `${value}`.replace(/"/g, '""');
  return /[",\n]/.test(normalized) ? `"${normalized}"` : normalized;
}

function downloadChartCsv(chart: ChartData, filename: string) {
  const header = ["Label", ...chart.series.map(series => series.label)];
  const rows = chart.labels.map((label, index) => [
    label,
    ...chart.series.map(series => series.values[index] ?? 0),
  ]);
  const csv = [header, ...rows].map(row => row.map(escapeCsvValue).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function SummaryCard({ label, value, detail, isLoading = false }: SummaryCardProps) {
  return (
    <div className="reporting-card rounded-[24px] bg-white p-6 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
      <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{label}</p>
      <div className="mt-3 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="reporting-title text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="reporting-muted mt-3 text-sm leading-6 text-[#667085]">{detail}</p>
    </div>
  );
}

function ChartCard({ data, language, currency, title, subtitle, summaryMode = "latest" }: { data: ChartData; language: AppLanguage; currency: string; title: string; subtitle: string; summaryMode?: "latest" | "total" }) {
  const chartId = useId().replace(/:/g, "");
  const width = 720;
  const height = 286;
  const paddingLeft = 56;
  const paddingRight = 32;
  const paddingTop = 24;
  const paddingBottom = 32;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;
  const allValues = data.series.flatMap(series => series.values);
  const axis = getChartAxis(allValues, data.unit);
  const paddedMax = axis.max;
  const paddedMin = axis.min;
  const xAxisLabelHeight = data.style === "bar" ? 56 : 36;
  const barBandWidth = data.style === "bar" ? plotWidth / Math.max(data.labels.length, 1) : 0;
  const barGroupWidth = data.style === "bar"
    ? Math.min(Math.max(barBandWidth * 0.72, 34), 110)
    : 0;
  const barGap = data.style === "bar" && data.series.length > 1 ? 8 : 0;
  const totalBarGap = data.style === "bar" ? barGap * Math.max(data.series.length - 1, 0) : 0;
  const barWidth = data.style === "bar"
    ? Math.max(Math.min((barGroupWidth - totalBarGap) / Math.max(data.series.length, 1), 34), 12)
    : 0;
  const hasData = allValues.some(value => value > 0);
  const chartTypeLabel = language === "ja"
    ? data.style === "line" ? "トレンド" : "内訳"
    : data.style === "line" ? "Trend" : "Breakdown";
  const emptyChartLabel = language === "ja" ? "レポートデータはまだありません" : "No report data yet";

  return (
    <div className="reporting-chart-card overflow-hidden rounded-[32px] bg-white shadow-[0_20px_56px_rgba(15,23,42,0.07)]">
      <div className="reporting-chart-header bg-white p-6 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{chartTypeLabel}</p>
            <h2 className="reporting-title mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
            <p className="reporting-muted mt-1 max-w-2xl text-sm leading-6 text-[#667085]">{subtitle}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {data.series.map(series => {
              const summaryValue = summaryMode === "total"
                ? series.values.reduce((total, value) => total + value, 0)
                : series.values.at(-1) ?? 0;

              return (
                <span key={series.label} className="reporting-chart-legend inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/90 px-3 py-2 text-[11px] font-semibold text-[#475467] shadow-[0_8px_18px_rgba(15,23,42,0.05)]">
                  <span className="h-2.5 w-2.5 rounded-full shadow-[0_0_0_3px_rgba(15,23,42,0.04)]" style={{ backgroundColor: series.color }} />
                  <span className="uppercase tracking-[0.14em]">{series.label}</span>
                  <span className="reporting-chart-legend-value rounded-full bg-[#f2f4f7] px-2 py-0.5 text-[#101828]">{formatAxisValue(summaryValue, data.unit, language, currency)}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="pb-4">
        <svg viewBox={`0 0 ${width} ${height + xAxisLabelHeight}`} className="w-full overflow-visible" fill="none" role="img" aria-label={title}>
          <title>{title}</title>
          <desc>{subtitle}</desc>
          <defs>
            {data.series.map((series, index) => (
              <linearGradient key={`${series.label}-area`} id={`${chartId}-area-${index}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={series.color} stopOpacity="0.24" />
                <stop offset="72%" stopColor={series.color} stopOpacity="0.06" />
                <stop offset="100%" stopColor={series.color} stopOpacity="0" />
              </linearGradient>
            ))}
            {data.series.map((series, index) => (
              <linearGradient key={`${series.label}-bar`} id={`${chartId}-bar-${index}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={series.color} stopOpacity="0.96" />
                <stop offset="100%" stopColor={series.color} stopOpacity="0.72" />
              </linearGradient>
            ))}
          </defs>

          <rect x={paddingLeft} y={paddingTop} width={plotWidth} height={plotHeight} fill="var(--reporting-chart-plot, #fcfcfd)" />
          {axis.ticks.map(axisValue => {
            const y = paddingTop + (plotHeight - ((axisValue - paddedMin) / Math.max(paddedMax - paddedMin, 1)) * plotHeight);

            return (
              <g key={axisValue}>
                <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke={axisValue === 0 ? "var(--reporting-chart-zero, #d0d5dd)" : "var(--reporting-chart-grid, #eaecf0)"} strokeDasharray={axisValue === 0 ? undefined : "4 8"} />
                <text x={paddingLeft - 12} y={y + 4} textAnchor="end" fill="var(--reporting-chart-axis, #98a2b3)" fontSize="11" fontWeight="700">
                  {formatAxisValue(axisValue, data.unit, language, currency)}
                </text>
              </g>
            );
          })}

          {data.labels.map((label, index) => {
            if (!shouldRenderXAxisLabel(index, data.labels.length, data.style)) {
              return null;
            }

            const x = data.style === "bar"
              ? paddingLeft + barBandWidth * (index + 0.5)
              : data.labels.length === 1
                ? paddingLeft + plotWidth / 2
                : paddingLeft + (plotWidth / Math.max(data.labels.length - 1, 1)) * index;

            return <line key={`${label}-${index}-tick`} x1={x} y1={paddingTop} x2={x} y2={paddingTop + plotHeight} stroke="var(--reporting-chart-tick, #f2f4f7)" />;
          })}

          {hasData ? (
            data.style === "line"
              ? data.series.map((series, index) => (
                  <g key={series.label}>
                    <path d={buildAreaPath(series.values, plotWidth, plotHeight, paddedMin, paddedMax)} transform={`translate(${paddingLeft} ${paddingTop})`} fill={`url(#${chartId}-area-${index})`} />
                    <path
                      d={buildSmoothLinePath(series.values, plotWidth, plotHeight, paddedMin, paddedMax)}
                      transform={`translate(${paddingLeft} ${paddingTop})`}
                      stroke={series.color}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="drop-shadow(0 6px 8px rgba(15, 23, 42, 0.10))"
                    />
                    {series.values.map((value, valueIndex) => {
                      const point = getChartPoint(value, valueIndex, data.labels.length, plotWidth, plotHeight, paddedMin, paddedMax);
                      const isEmphasized = valueIndex === 0 || valueIndex === series.values.length - 1 || data.labels.length <= 10;

                      if (!isEmphasized) {
                        return null;
                      }

                      return <circle key={`${series.label}-${data.labels[valueIndex]}`} cx={paddingLeft + point.x} cy={paddingTop + point.y} r={valueIndex === series.values.length - 1 ? "5.5" : "4"} fill="var(--reporting-chart-point, #ffffff)" stroke={series.color} strokeWidth="3" />;
                    })}
                  </g>
                ))
              : data.labels.map((label, labelIndex) => {
                  const xCenter = paddingLeft + barBandWidth * (labelIndex + 0.5);
                  const groupStart = xCenter - (barWidth * data.series.length + totalBarGap) / 2;

                  return (
                    <g key={label}>
                      {data.series.map((series, seriesIndex) => {
                        const value = series.values[labelIndex] ?? 0;
                        const rawBarHeight = Math.max(0, ((value - paddedMin) / Math.max(paddedMax - paddedMin, 1)) * plotHeight);

                        if (rawBarHeight <= 0.5) {
                          return null;
                        }

                        const barHeight = Math.min(plotHeight, Math.max(rawBarHeight, 4));
                        const x = groupStart + seriesIndex * (barWidth + barGap);
                        const y = paddingTop + plotHeight - barHeight;

                        return (
                          <g key={`${series.label}-${label}`}>
                            <rect x={x} y={y} width={barWidth} height={barHeight} rx="9" fill={`url(#${chartId}-bar-${seriesIndex})`} filter="drop-shadow(0 8px 10px rgba(15, 23, 42, 0.10))" />
                            {barBandWidth >= 40 ? (
                              <text x={x + barWidth / 2} y={Math.max(y - 7, paddingTop + 12)} textAnchor="middle" fill="var(--reporting-chart-value, #475467)" fontSize="10" fontWeight="800">
                                {formatAxisValue(value, data.unit, language, currency)}
                              </text>
                            ) : null}
                          </g>
                        );
                      })}
                    </g>
                  );
                })
          ) : (
            <g>
              <rect x={paddingLeft + plotWidth / 2 - 94} y={paddingTop + plotHeight / 2 - 28} width="188" height="56" rx="18" fill="var(--reporting-chart-empty-bg, #ffffff)" stroke="var(--reporting-chart-grid, #eaecf0)" />
              <text x={paddingLeft + plotWidth / 2} y={paddingTop + plotHeight / 2 + 4} textAnchor="middle" fill="var(--reporting-chart-empty-text, #667085)" fontSize="12" fontWeight="800">
                {emptyChartLabel}
              </text>
            </g>
          )}

          {data.labels.map((label, index) => {
            if (!shouldRenderXAxisLabel(index, data.labels.length, data.style)) {
              return null;
            }

            const x = data.style === "bar"
              ? paddingLeft + barBandWidth * (index + 0.5)
              : data.labels.length === 1
                ? paddingLeft + plotWidth / 2
                : paddingLeft + (plotWidth / Math.max(data.labels.length - 1, 1)) * index;
            const labelLines = data.style === "bar"
              ? getAxisLabelLines(label, 2, Math.max(12, Math.floor(barBandWidth / 7)))
              : [label];

            return (
              <text key={`${label}-${index}`} x={x} y={height + 22} textAnchor="middle" fill="var(--reporting-chart-axis, #98a2b3)" fontSize="11" fontWeight="800">
                {labelLines.map((line, lineIndex) => (
                  <tspan key={`${label}-${lineIndex}`} x={x} dy={lineIndex === 0 ? 0 : 14}>
                    {line}
                  </tspan>
                ))}
              </text>
            );
          })}

        </svg>
      </div>
    </div>
  );
}

export function SimplifiedReportingView({ customers, currency, expenses, vendors, isLoadingReporting = false, language, productPlans, tickets, canViewAllExpenses = false, members = [] }: SimplifiedReportingViewProps) {
  const copy: ReportingViewCopy = reportingViewCopy[language];
  const expenseCopy = language === "ja"
    ? {
        reportTypeOption: "経費",
        metricLabel: "経費指標",
        spendLabel: "支出",
        metrics: { byVendor: "ベンダー別支出", byMonth: "月別支出", byCustomer: "顧客別支出" },
        vendorChartTitle: "ベンダー別支出",
        vendorChartSubtitle: "選択した期間のベンダー別の経費合計。",
        monthChartTitle: "月別支出",
        monthChartSubtitle: "選択した期間の月ごとの経費合計。",
        customerChartTitle: "顧客別支出",
        customerChartSubtitle: "選択した期間の顧客別の経費合計。",
        unknownVendor: "不明なベンダー",
        unlinkedCustomer: "顧客なし",
        memberLabel: "メンバー",
        allMembers: "全メンバー",
        unknownMember: "不明",
      }
    : {
        reportTypeOption: "Expenses",
        metricLabel: "Expense metric",
        spendLabel: "Spend",
        metrics: { byVendor: "Spend by vendor", byMonth: "Spend by month", byCustomer: "Spend by customer" },
        vendorChartTitle: "Spend by vendor",
        vendorChartSubtitle: "Total expense amount grouped by vendor for the selected range.",
        monthChartTitle: "Spend by month",
        monthChartSubtitle: "Monthly expense totals across the selected range.",
        customerChartTitle: "Spend by customer",
        customerChartSubtitle: "Total expense amount grouped by linked customer for the selected range.",
        unknownVendor: "Unknown vendor",
        unlinkedCustomer: "No customer",
        memberLabel: "Member",
        allMembers: "All members",
        unknownMember: "Unknown",
      };
  const defaultRange = getDateRangeBounds(tickets, customers);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [ticketMetric, setTicketMetric] = useState<TicketMetric>("openedClosed");
  const [groupByAssignee, setGroupByAssignee] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [slaBrokenOnly, setSlaBrokenOnly] = useState(false);
  const [scheduledOnly, setScheduledOnly] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("all");
  const [productMetric, setProductMetric] = useState<ProductMetric>("customers");
  const [productChartMode, setProductChartMode] = useState<ProductChartMode>("snapshot");
  const [expenseMetric, setExpenseMetric] = useState<ExpenseMetric>("byVendor");
  const [expenseOwnerFilter, setExpenseOwnerFilter] = useState<string>("all");
  const [primaryReportView, setPrimaryReportView] = useState<PrimaryReportView>("tickets");

  const rangeStart = Math.min(parseDateInput(startDate), parseDateInput(endDate));
  const rangeEnd = Math.max(parseDateInput(startDate, true), parseDateInput(endDate, true));
  const now = Date.now();

  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      if (selectedCustomerId !== "all" && ticket.customerId !== selectedCustomerId) {
        return false;
      }

      if (scheduledOnly && !ticket.scheduleAt) {
        return false;
      }

      if (slaBrokenOnly && !isSlaBroken(ticket, now)) {
        return false;
      }

      return true;
    });
  }, [now, scheduledOnly, selectedCustomerId, slaBrokenOnly, tickets]);

  const ticketChart = useMemo<ChartData>(() => {
    const shouldGroup = groupByAssignee || groupByCategory;

    if (shouldGroup) {
      const grouped = new Map<string, { opened: number; closed: number; responseTotal: number; responseCount: number }>();

      for (const ticket of filteredTickets) {
        const groupLabel = [
          groupByAssignee ? ticket.assignee ?? copy.labels.unassigned : null,
          groupByCategory ? ticket.category : null,
        ].filter(Boolean).join(" • ");
        const current = grouped.get(groupLabel) ?? { opened: 0, closed: 0, responseTotal: 0, responseCount: 0 };
        const createdAt = parseTimestamp(ticket.createdAt);
        const closedAt = parseTimestamp(ticket.lastUpdate);

        if (createdAt >= rangeStart && createdAt <= rangeEnd) {
          current.opened += 1;

          const responseHours = getFirstResponseHours(ticket);

          if (responseHours !== null) {
            current.responseTotal += responseHours;
            current.responseCount += 1;
          }
        }

        if (ticket.status === "Resolved" && closedAt >= rangeStart && closedAt <= rangeEnd) {
          current.closed += 1;
        }

        grouped.set(groupLabel, current);
      }

      const rows = [...grouped.entries()]
        .map(([label, values]) => ({
          label,
          closed: values.closed,
          opened: values.opened,
          response: values.responseCount > 0 ? values.responseTotal / values.responseCount : 0,
        }))
        .sort((left, right) => {
          if (ticketMetric === "closed") {
            return right.closed - left.closed;
          }

          if (ticketMetric === "firstResponse") {
            return right.response - left.response;
          }

          return right.opened + right.closed - (left.opened + left.closed);
        })
        .slice(0, 8);

      if (ticketMetric === "opened") {
        return {
          labels: rows.map(row => row.label),
          series: [{ color: chartColors[0]!, label: copy.labels.opened, values: rows.map(row => row.opened) }],
          style: "bar",
          unit: "count",
        };
      }

      if (ticketMetric === "closed") {
        return {
          labels: rows.map(row => row.label),
          series: [{ color: chartColors[1]!, label: copy.labels.closed, values: rows.map(row => row.closed) }],
          style: "bar",
          unit: "count",
        };
      }

      if (ticketMetric === "firstResponse") {
        return {
          labels: rows.map(row => row.label),
          series: [{ color: chartColors[2]!, label: copy.labels.avgFirstResponse, values: rows.map(row => row.response) }],
          style: "bar",
          unit: "hours",
        };
      }

      return {
        labels: rows.map(row => row.label),
        series: [
          { color: chartColors[0]!, label: copy.labels.opened, values: rows.map(row => row.opened) },
          { color: chartColors[1]!, label: copy.labels.closed, values: rows.map(row => row.closed) },
        ],
        style: "bar",
        unit: "count",
      };
    }

    const buckets = createDailyBuckets(rangeStart, rangeEnd, language);
    const openedValues = buckets.map(() => 0);
    const closedValues = buckets.map(() => 0);
    const responseTotals = buckets.map(() => 0);
    const responseCounts = buckets.map(() => 0);

    for (const ticket of filteredTickets) {
      const createdAt = parseTimestamp(ticket.createdAt);
      const closedAt = parseTimestamp(ticket.lastUpdate);
      const createdBucketIndex = buckets.findIndex(bucket => createdAt >= bucket.start && createdAt <= bucket.end);

      if (createdBucketIndex >= 0) {
        openedValues[createdBucketIndex] = (openedValues[createdBucketIndex] ?? 0) + 1;

        const responseHours = getFirstResponseHours(ticket);

        if (responseHours !== null) {
          responseTotals[createdBucketIndex] = (responseTotals[createdBucketIndex] ?? 0) + responseHours;
          responseCounts[createdBucketIndex] = (responseCounts[createdBucketIndex] ?? 0) + 1;
        }
      }

      if (ticket.status === "Resolved") {
        const closedBucketIndex = buckets.findIndex(bucket => closedAt >= bucket.start && closedAt <= bucket.end);

        if (closedBucketIndex >= 0) {
          closedValues[closedBucketIndex] = (closedValues[closedBucketIndex] ?? 0) + 1;
        }
      }
    }

    if (ticketMetric === "opened") {
      return {
        labels: buckets.map(bucket => bucket.label),
        series: [{ color: chartColors[0]!, label: copy.labels.opened, values: openedValues }],
        style: "line",
        unit: "count",
      };
    }

    if (ticketMetric === "closed") {
      return {
        labels: buckets.map(bucket => bucket.label),
        series: [{ color: chartColors[1]!, label: copy.labels.closed, values: closedValues }],
        style: "line",
        unit: "count",
      };
    }

    if (ticketMetric === "firstResponse") {
      return {
        labels: buckets.map(bucket => bucket.label),
        series: [{
          color: chartColors[2]!,
          label: copy.labels.avgFirstResponse,
          values: buckets.map((_, index) => {
            const count = responseCounts[index] ?? 0;
            const total = responseTotals[index] ?? 0;

            return count > 0 ? total / count : 0;
          }),
        }],
        style: "line",
        unit: "hours",
      };
    }

    return {
      labels: buckets.map(bucket => bucket.label),
      series: [
        { color: chartColors[0]!, label: copy.labels.opened, values: openedValues },
        { color: chartColors[1]!, label: copy.labels.closed, values: closedValues },
      ],
      style: "line",
      unit: "count",
    };
  }, [copy, filteredTickets, groupByAssignee, groupByCategory, language, rangeEnd, rangeStart, ticketMetric]);

  const customerTimeline = useMemo(() => {
    const buckets = createDailyBuckets(rangeStart, rangeEnd, language);
    const growthValues = buckets.map(() => 0);
    const spendValues = buckets.map(() => 0);
    const spendByCustomer = new Map(customers.map(customer => [customer.id, getEstimatedCustomerMonthlySpend(customer, productPlans)]));

    for (let index = 0; index < buckets.length; index += 1) {
      const bucket = buckets[index]!;
      const activeCustomers = customers.filter(customer => getCustomerStartTimestamp(customer) <= bucket.end);

      growthValues[index] = activeCustomers.length;
      spendValues[index] = activeCustomers.reduce((total, customer) => total + (spendByCustomer.get(customer.id) ?? 0), 0);
    }

    return {
      growth: {
        labels: buckets.map(bucket => bucket.label),
        series: [{ color: chartColors[0]!, label: copy.labels.customers, values: growthValues }],
        style: "line" as const,
        unit: "count" as const,
      },
      spend: {
        labels: buckets.map(bucket => bucket.label),
        series: [{ color: chartColors[3]!, label: copy.labels.estimatedMonthlySpend, values: spendValues }],
        style: "line" as const,
        unit: "currency" as const,
      },
      totalMonthlySpend: spendValues[spendValues.length - 1] ?? 0,
    };
  }, [copy, customers, language, productPlans, rangeEnd, rangeStart]);

  const summaryCards = useMemo(() => {
    const openedInRange = filteredTickets.filter(ticket => {
      const createdAt = parseTimestamp(ticket.createdAt);
      return createdAt >= rangeStart && createdAt <= rangeEnd;
    }).length;

    const closedInRange = filteredTickets.filter(ticket => {
      const lastUpdate = parseTimestamp(ticket.lastUpdate);
      return ticket.status === "Resolved" && lastUpdate >= rangeStart && lastUpdate <= rangeEnd;
    }).length;

    const firstResponseSamples = filteredTickets
      .map(getFirstResponseHours)
      .filter((value): value is number => value !== null);
    const averageFirstResponse = firstResponseSamples.length > 0
      ? firstResponseSamples.reduce((total, value) => total + value, 0) / firstResponseSamples.length
      : 0;
    const brokenTickets = filteredTickets.filter(ticket => isSlaBroken(ticket, now)).length;

    return [
      {
        label: copy.tickets.summary.opened.label,
        value: `${openedInRange}`,
        detail: copy.tickets.summary.opened.detail,
      },
      {
        label: copy.tickets.summary.closed.label,
        value: `${closedInRange}`,
        detail: copy.tickets.summary.closed.detail,
      },
      {
        label: copy.tickets.summary.firstResponse.label,
        value: formatHours(averageFirstResponse, language),
        detail: copy.tickets.summary.firstResponse.detail,
      },
      {
        label: copy.tickets.summary.slaBroken.label,
        value: `${brokenTickets}`,
        detail: copy.tickets.summary.slaBroken.detail,
      },
    ] satisfies SummaryCardProps[];
  }, [copy, filteredTickets, language, now, rangeEnd, rangeStart]);

  const customerSummary = useMemo(() => {
    const netNewCustomers = customers.filter(customer => {
      const startTimestamp = getCustomerStartTimestamp(customer);
      return startTimestamp >= rangeStart && startTimestamp <= rangeEnd;
    }).length;

    return [
      {
        label: copy.customers.summary.customers.label,
        value: `${customerTimeline.growth.series[0]?.values.at(-1) ?? 0}`,
        detail: copy.customers.summary.customers.detail,
      },
      {
        label: copy.customers.summary.growth.label,
        value: `${netNewCustomers}`,
        detail: copy.customers.summary.growth.detail,
      },
    ] satisfies SummaryCardProps[];
  }, [copy, currency, customerTimeline, customers, language, rangeEnd, rangeStart]);

  const customerProductReporting = useMemo(() => {
    const lookup = getProductPlanLookup(productPlans);
    const buckets = createDailyBuckets(rangeStart, rangeEnd, language);
    const scopedCustomers = customers.filter(customer => {
      if (selectedCustomerId !== "all" && customer.id !== selectedCustomerId) {
        return false;
      }

      return getCustomerStartTimestamp(customer) <= rangeEnd;
    });

    const productStats = new Map<string, { customers: number; spend: number; seats: number }>();

    for (const customer of scopedCustomers) {
      const countedProducts = new Set<string>();

      for (const product of customer.products) {
        const productStart = getCustomerProductStartTimestamp(customer, product);

        if (productStart < rangeStart || productStart > rangeEnd) {
          continue;
        }

        const label = product.name.trim() || copy.products.unnamedProduct;
        const current = productStats.get(label) ?? { customers: 0, spend: 0, seats: 0 };

        if (!countedProducts.has(label)) {
          current.customers += 1;
          countedProducts.add(label);
        }

        current.seats += Math.max(product.seats, 0);
        current.spend += getEstimatedProductMonthlySpend(product, lookup);
        productStats.set(label, current);
      }
    }

    const rows = [...productStats.entries()]
      .map(([label, values]) => ({ label, ...values }))
      .sort((left, right) => right.spend - left.spend || right.customers - left.customers || left.label.localeCompare(right.label));

    const adoptionRows = [...rows]
      .sort((left, right) => right.customers - left.customers || right.seats - left.seats || left.label.localeCompare(right.label))
      .slice(0, 8);
    const spendRows = (rows.some(row => row.spend > 0) ? rows.filter(row => row.spend > 0) : rows).slice(0, 8);
    const customerRows = scopedCustomers
      .map(customer => {
        const productsInRange = customer.products.filter(product => {
          const productStart = getCustomerProductStartTimestamp(customer, product);
          return productStart >= rangeStart && productStart <= rangeEnd;
        });

        return {
          company: customer.company,
          productCount: productsInRange.length,
          products: productsInRange.map(product => product.name),
          spend: productsInRange.reduce((total, product) => total + getEstimatedProductMonthlySpend(product, lookup), 0),
        };
      })
      .filter(customer => customer.productCount > 0 || customer.spend > 0)
      .sort((left, right) => right.spend - left.spend || right.productCount - left.productCount || left.company.localeCompare(right.company));
    const topAdoptionProductLabels = adoptionRows.slice(0, 4).map(row => row.label);
    const topSpendProductLabels = spendRows.slice(0, 4).map(row => row.label);
    const topSeatProductLabels = [...rows]
      .sort((left, right) => right.seats - left.seats || right.customers - left.customers || left.label.localeCompare(right.label))
      .slice(0, 4)
      .map(row => row.label);
    const topCustomerLabels = customerRows.slice(0, 4).map(row => row.company);

    const buildProductTimelineSeries = (labels: string[], mode: "customers" | "monthlySpend" | "seats") => labels.map((label, index) => ({
      color: chartColors[index % chartColors.length]!,
      label,
      values: buckets.map(bucket => scopedCustomers.reduce((total, customer) => {
        const countedProducts = new Set<string>();

        return total + customer.products.reduce((productTotal, product) => {
          const productLabel = product.name.trim() || copy.products.unnamedProduct;
          const productStart = getCustomerProductStartTimestamp(customer, product);

          if (productLabel !== label || productStart > bucket.end) {
            return productTotal;
          }

          if (mode === "monthlySpend") {
            return productTotal + getEstimatedProductMonthlySpend(product, lookup);
          }

          if (mode === "seats") {
            return productTotal + Math.max(product.seats, 0);
          }

          if (countedProducts.has(productLabel)) {
            return productTotal;
          }

          countedProducts.add(productLabel);
          return productTotal + 1;
        }, 0);
      }, 0)),
    }));

    const buildCustomerSpendTimelineSeries = (labels: string[]) => labels.map((label, index) => ({
      color: chartColors[index % chartColors.length]!,
      label,
      values: buckets.map(bucket => {
        const customer = scopedCustomers.find(item => item.company === label);

        if (!customer) {
          return 0;
        }

        return customer.products.reduce((total, product) => {
          const productStart = getCustomerProductStartTimestamp(customer, product);
          return productStart <= bucket.end ? total + getEstimatedProductMonthlySpend(product, lookup) : total;
        }, 0);
      }),
    }));

    return {
      adoptionChart: {
        labels: adoptionRows.map(row => row.label),
        series: [{ color: chartColors[0]!, label: copy.labels.customers, values: adoptionRows.map(row => row.customers) }],
        style: "bar" as const,
        unit: "count" as const,
      },
      spendChart: {
        labels: spendRows.map(row => row.label),
        series: [{ color: chartColors[3]!, label: copy.labels.monthlySpend, values: spendRows.map(row => row.spend) }],
        style: "bar" as const,
        unit: "currency" as const,
      },
      seatsChart: {
        labels: adoptionRows.map(row => row.label),
        series: [{ color: chartColors[1]!, label: copy.labels.seats, values: adoptionRows.map(row => row.seats) }],
        style: "bar" as const,
        unit: "count" as const,
      },
      customerSpendChart: {
        labels: customerRows.slice(0, 8).map(row => row.company),
        series: [{ color: chartColors[2]!, label: copy.labels.monthlySpend, values: customerRows.slice(0, 8).map(row => row.spend) }],
        style: "bar" as const,
        unit: "currency" as const,
      },
      adoptionTimelineChart: {
        labels: buckets.map(bucket => bucket.label),
        series: buildProductTimelineSeries(topAdoptionProductLabels, "customers"),
        style: "line" as const,
        unit: "count" as const,
      },
      spendTimelineChart: {
        labels: buckets.map(bucket => bucket.label),
        series: buildProductTimelineSeries(topSpendProductLabels, "monthlySpend"),
        style: "line" as const,
        unit: "currency" as const,
      },
      seatsTimelineChart: {
        labels: buckets.map(bucket => bucket.label),
        series: buildProductTimelineSeries(topSeatProductLabels, "seats"),
        style: "line" as const,
        unit: "count" as const,
      },
      customerSpendTimelineChart: {
        labels: buckets.map(bucket => bucket.label),
        series: buildCustomerSpendTimelineSeries(topCustomerLabels),
        style: "line" as const,
        unit: "currency" as const,
      },
      productSummary: [
        {
          label: copy.products.summary.productsInUse.label,
          value: `${rows.length}`,
          detail: copy.products.summary.productsInUse.detail,
        },
        {
          label: copy.products.summary.customersWithProducts.label,
          value: `${customerRows.length}`,
          detail: copy.products.summary.customersWithProducts.detail,
        },
        {
          label: copy.products.summary.mappedMonthlySpend.label,
          value: formatCurrency(rows.reduce((total, row) => total + row.spend, 0), language, currency),
          detail: copy.products.summary.mappedMonthlySpend.detail,
        },
      ] satisfies SummaryCardProps[],
      topCustomers: customerRows.slice(0, 6),
    };
  }, [copy, currency, customers, language, productPlans, rangeEnd, rangeStart, selectedCustomerId]);

  const productChartConfig = useMemo(() => {
    const isTimeline = productChartMode === "timeline";

    switch (productMetric) {
      case "monthlySpend":
        return {
          chart: isTimeline ? customerProductReporting.spendTimelineChart : customerProductReporting.spendChart,
          title: isTimeline ? copy.products.charts.monthlySpend.timelineTitle : copy.products.charts.monthlySpend.snapshotTitle,
          subtitle: isTimeline ? copy.products.charts.monthlySpend.timelineSubtitle : copy.products.charts.monthlySpend.snapshotSubtitle,
          filename: `product-spend-${isTimeline ? "timeline" : "snapshot"}-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
      case "seats":
        return {
          chart: isTimeline ? customerProductReporting.seatsTimelineChart : customerProductReporting.seatsChart,
          title: isTimeline ? copy.products.charts.seats.timelineTitle : copy.products.charts.seats.snapshotTitle,
          subtitle: isTimeline ? copy.products.charts.seats.timelineSubtitle : copy.products.charts.seats.snapshotSubtitle,
          filename: `product-seats-${isTimeline ? "timeline" : "snapshot"}-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
      case "customerSpend":
        return {
          chart: isTimeline ? customerProductReporting.customerSpendTimelineChart : customerProductReporting.customerSpendChart,
          title: isTimeline ? copy.products.charts.customerSpend.timelineTitle : copy.products.charts.customerSpend.snapshotTitle,
          subtitle: isTimeline ? copy.products.charts.customerSpend.timelineSubtitle : copy.products.charts.customerSpend.snapshotSubtitle,
          filename: `customer-spend-${isTimeline ? "timeline" : "snapshot"}-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
      case "customers":
      default:
        return {
          chart: isTimeline ? customerProductReporting.adoptionTimelineChart : customerProductReporting.adoptionChart,
          title: isTimeline ? copy.products.charts.customers.timelineTitle : copy.products.charts.customers.snapshotTitle,
          subtitle: isTimeline ? copy.products.charts.customers.timelineSubtitle : copy.products.charts.customers.snapshotSubtitle,
          filename: `product-adoption-${isTimeline ? "timeline" : "snapshot"}-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
    }
  }, [copy, customerProductReporting, endDate, productChartMode, productMetric, startDate]);

  const ticketChartConfig = useMemo(() => {
    return {
      chart: ticketChart,
      title: copy.tickets.title,
      subtitle: groupByAssignee || groupByCategory ? copy.tickets.groupedSubtitle : copy.tickets.trendSubtitle,
      filename: `ticket-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
    };
  }, [copy, endDate, groupByAssignee, groupByCategory, startDate, ticketChart]);

  const expenseMemberNameByEmail = useMemo(
    () => new Map(members.map(member => [member.email.trim().toLowerCase(), member.name])),
    [members],
  );

  const resolveExpenseMemberName = useMemo(
    () => (email: string | null | undefined) => {
      const normalized = (email ?? "").trim();
      if (!normalized) {
        return expenseCopy.unknownMember;
      }
      return expenseMemberNameByEmail.get(normalized.toLowerCase()) ?? normalized;
    },
    [expenseCopy.unknownMember, expenseMemberNameByEmail],
  );

  const expenseOwnerOptions = useMemo(() => {
    const owners = new Map<string, string>();
    for (const expense of expenses) {
      const normalized = (expense.createdBy ?? "").trim();
      const value = normalized.toLowerCase();
      if (!value) {
        continue;
      }
      if (!owners.has(value)) {
        owners.set(value, resolveExpenseMemberName(normalized));
      }
    }
    return [...owners.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [expenses, resolveExpenseMemberName]);

  const expenseReporting = useMemo(() => {
    const vendorNameById = new Map(vendors.map(vendor => [vendor.id, vendor.name]));
    const customerNameById = new Map(customers.map(customer => [customer.id, customer.company]));
    const inRange = expenses.filter(expense => {
      const timestamp = parseDateInput(expense.expenseDate);
      if (!Number.isFinite(timestamp) || timestamp < rangeStart || timestamp > rangeEnd) {
        return false;
      }
      if (canViewAllExpenses && expenseOwnerFilter !== "all" && (expense.createdBy ?? "").trim().toLowerCase() !== expenseOwnerFilter) {
        return false;
      }
      return true;
    });

    const vendorTotals = new Map<string, number>();
    for (const expense of inRange) {
      vendorTotals.set(expense.vendorId, (vendorTotals.get(expense.vendorId) ?? 0) + expense.amount);
    }
    const byVendor = [...vendorTotals.entries()]
      .map(([vendorId, total]) => ({ name: vendorNameById.get(vendorId) ?? expenseCopy.unknownVendor, total }))
      .sort((left, right) => right.total - left.total);

    const monthTotals = new Map<string, number>();
    for (const expense of inRange) {
      const monthKey = expense.expenseDate.slice(0, 7);
      monthTotals.set(monthKey, (monthTotals.get(monthKey) ?? 0) + expense.amount);
    }
    const byMonth = [...monthTotals.entries()]
      .map(([monthKey, total]) => ({ monthKey, total }))
      .sort((left, right) => right.monthKey.localeCompare(left.monthKey));

    const customerTotals = new Map<string, number>();
    for (const expense of inRange) {
      const customerId = (expense.customerId ?? "").trim();
      customerTotals.set(customerId, (customerTotals.get(customerId) ?? 0) + expense.amount);
    }
    const byCustomer = [...customerTotals.entries()]
      .map(([customerId, total]) => ({ name: customerId ? (customerNameById.get(customerId) ?? expenseCopy.unlinkedCustomer) : expenseCopy.unlinkedCustomer, total }))
      .sort((left, right) => right.total - left.total);

    const topVendors = byVendor.slice(0, 8);
    const monthsAscending = [...byMonth].reverse();
    const topCustomers = byCustomer.slice(0, 8);

    return {
      byVendorChart: {
        labels: topVendors.map(row => row.name),
        series: [{ color: chartColors[0]!, label: expenseCopy.spendLabel, values: topVendors.map(row => row.total) }],
        style: "bar" as const,
        unit: "currency" as const,
      } satisfies ChartData,
      byMonthChart: {
        labels: monthsAscending.map(row => formatExpenseMonth(row.monthKey, language)),
        series: [{ color: chartColors[1]!, label: expenseCopy.spendLabel, values: monthsAscending.map(row => row.total) }],
        style: "line" as const,
        unit: "currency" as const,
      } satisfies ChartData,
      byCustomerChart: {
        labels: topCustomers.map(row => row.name),
        series: [{ color: chartColors[0]!, label: expenseCopy.spendLabel, values: topCustomers.map(row => row.total) }],
        style: "bar" as const,
        unit: "currency" as const,
      } satisfies ChartData,
    };
  }, [expenses, vendors, customers, rangeStart, rangeEnd, language, expenseCopy.spendLabel, expenseCopy.unknownVendor, expenseCopy.unlinkedCustomer, canViewAllExpenses, expenseOwnerFilter]);

  const expenseChartConfig = useMemo(() => {
    switch (expenseMetric) {
      case "byMonth":
        return {
          chart: expenseReporting.byMonthChart,
          title: expenseCopy.monthChartTitle,
          subtitle: expenseCopy.monthChartSubtitle,
          filename: `expense-by-month-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
      case "byCustomer":
        return {
          chart: expenseReporting.byCustomerChart,
          title: expenseCopy.customerChartTitle,
          subtitle: expenseCopy.customerChartSubtitle,
          filename: `expense-by-customer-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
      case "byVendor":
      default:
        return {
          chart: expenseReporting.byVendorChart,
          title: expenseCopy.vendorChartTitle,
          subtitle: expenseCopy.vendorChartSubtitle,
          filename: `expense-by-vendor-report-${startDate || "start"}-to-${endDate || "end"}.csv`,
        };
    }
  }, [expenseMetric, expenseReporting, expenseCopy, endDate, startDate]);

  const primaryChartConfig = primaryReportView === "tickets"
    ? ticketChartConfig
    : primaryReportView === "products"
      ? productChartConfig
      : expenseChartConfig;

  const handleExportCsv = () => {
    downloadChartCsv(primaryChartConfig.chart, primaryChartConfig.filename);
  };

  return (
    <div className="reporting-view p-8">
      <section className="mb-8">
        <div>
          <p className="reporting-eyebrow text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="reporting-title mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="reporting-muted mt-3 max-w-3xl text-sm text-[#667085]">
            {copy.page.description}
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {summaryCards.map(card => (
          <SummaryCard key={card.label} {...card} isLoading={isLoadingReporting} />
        ))}
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <div className="reporting-card rounded-[28px] bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="p-2 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.eyebrow}</p>
                <h2 className="reporting-title mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.filters.title}</h2>
              </div>
              <button
                type="button"
                onClick={handleExportCsv}
                className="rounded-full bg-[#101828] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-[#1d2939]"
              >
                {copy.filters.exportCsv}
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <label className="reporting-filter-field rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828]">
                <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.reportType}</span>
                <select value={primaryReportView} onChange={event => setPrimaryReportView(event.target.value as PrimaryReportView)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                  <option value="tickets">{copy.filters.reportTypes.tickets}</option>
                  <option value="products">{copy.filters.reportTypes.products}</option>
                  <option value="expenses">{expenseCopy.reportTypeOption}</option>
                </select>
              </label>
              <label className="reporting-filter-field rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828]">
                <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.startDate}</span>
                <input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none" />
              </label>
              <label className="reporting-filter-field rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828]">
                <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.endDate}</span>
                <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none" />
              </label>
              <label className="reporting-filter-field rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828]">
                <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{primaryReportView === "tickets" ? copy.filters.ticketMetric : primaryReportView === "products" ? copy.filters.productMetric : expenseCopy.metricLabel}</span>
                {primaryReportView === "tickets" ? (
                  <select value={ticketMetric} onChange={event => setTicketMetric(event.target.value as TicketMetric)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                    <option value="openedClosed">{copy.filters.ticketMetrics.openedClosed}</option>
                    <option value="opened">{copy.filters.ticketMetrics.opened}</option>
                    <option value="closed">{copy.filters.ticketMetrics.closed}</option>
                    <option value="firstResponse">{copy.filters.ticketMetrics.firstResponse}</option>
                  </select>
                ) : primaryReportView === "products" ? (
                  <select value={productMetric} onChange={event => setProductMetric(event.target.value as ProductMetric)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                    <option value="customers">{copy.filters.productMetrics.customers}</option>
                    <option value="monthlySpend">{copy.filters.productMetrics.monthlySpend}</option>
                    <option value="seats">{copy.filters.productMetrics.seats}</option>
                    <option value="customerSpend">{copy.filters.productMetrics.customerSpend}</option>
                  </select>
                ) : (
                  <select value={expenseMetric} onChange={event => setExpenseMetric(event.target.value as ExpenseMetric)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                    <option value="byVendor">{expenseCopy.metrics.byVendor}</option>
                    <option value="byMonth">{expenseCopy.metrics.byMonth}</option>
                    <option value="byCustomer">{expenseCopy.metrics.byCustomer}</option>
                  </select>
                )}
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-stretch gap-3 text-sm text-[#475467]">
              {primaryReportView !== "expenses" ? (
                <label className="reporting-filter-field min-w-[180px] flex-1 rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828] lg:min-w-[180px] lg:max-w-[240px] lg:flex-[0.8]">
                  <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.customer}</span>
                  <select value={selectedCustomerId} onChange={event => setSelectedCustomerId(event.target.value)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                    <option value="all">{copy.filters.allCustomers}</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {primaryReportView === "expenses" && canViewAllExpenses ? (
                <label className="reporting-filter-field min-w-[180px] flex-1 rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828] lg:min-w-[180px] lg:max-w-[240px] lg:flex-[0.8]">
                  <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{expenseCopy.memberLabel}</span>
                  <select value={expenseOwnerFilter} onChange={event => setExpenseOwnerFilter(event.target.value)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                    <option value="all">{expenseCopy.allMembers}</option>
                    {expenseOwnerOptions.map(owner => (
                      <option key={owner.value} value={owner.value}>
                        {owner.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {primaryReportView === "tickets" ? (
                <div className="reporting-filter-field min-w-[320px] flex-[2] rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828] lg:flex-[2.8]">
                  <p className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.ticketOptions}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-sm text-[#475467]">
                    <label className="reporting-chip inline-flex items-center gap-2 rounded-full bg-white px-4 py-2">
                      <input type="checkbox" checked={groupByAssignee} onChange={event => setGroupByAssignee(event.target.checked)} />
                      {copy.filters.ticketOptionsLabels.assignee}
                    </label>
                    <label className="reporting-chip inline-flex items-center gap-2 rounded-full bg-white px-4 py-2">
                      <input type="checkbox" checked={groupByCategory} onChange={event => setGroupByCategory(event.target.checked)} />
                      {copy.filters.ticketOptionsLabels.category}
                    </label>
                    <label className="reporting-chip inline-flex items-center gap-2 rounded-full bg-white px-4 py-2">
                      <input type="checkbox" checked={slaBrokenOnly} onChange={event => setSlaBrokenOnly(event.target.checked)} />
                      {copy.filters.ticketOptionsLabels.slaBroken}
                    </label>
                    <label className="reporting-chip inline-flex items-center gap-2 rounded-full bg-white px-4 py-2">
                      <input type="checkbox" checked={scheduledOnly} onChange={event => setScheduledOnly(event.target.checked)} />
                      {copy.filters.ticketOptionsLabels.scheduled}
                    </label>
                  </div>
                </div>
              ) : primaryReportView === "products" ? (
                <>
                  <label className="reporting-filter-field min-w-[180px] flex-1 rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-semibold text-[#101828] lg:max-w-[240px] lg:flex-none lg:w-[220px]">
                    <span className="reporting-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">{copy.filters.chartMode}</span>
                    <select value={productChartMode} onChange={event => setProductChartMode(event.target.value as ProductChartMode)} className="reporting-control mt-2 block w-full rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none">
                      <option value="snapshot">{copy.filters.chartModeOptions.snapshot}</option>
                      <option value="timeline">{copy.filters.chartModeOptions.timeline}</option>
                    </select>
                  </label>

                </>
              ) : null}
            </div>
          </div>

          <ChartCard data={primaryChartConfig.chart} language={language} currency={currency} title={primaryChartConfig.title} subtitle={primaryChartConfig.subtitle} summaryMode={primaryReportView === "expenses" ? "total" : "latest"} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartCard data={customerTimeline.growth} language={language} currency={currency} title={copy.customers.growthChart.title} subtitle={copy.customers.growthChart.subtitle} />
            <ChartCard data={customerTimeline.spend} language={language} currency={currency} title={copy.customers.spendChart.title} subtitle={copy.customers.spendChart.subtitle} />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <div className="reporting-card rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.customers.panel.eyebrow}</p>
                <h2 className="reporting-title mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.customers.panel.title}</h2>
              </div>
              <span className="reporting-badge reporting-badge--green rounded-full bg-[#f4fbf7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#006949]">{copy.customers.panel.badge}</span>
            </div>

            <div className="mt-5 space-y-4">
              {customerSummary.map(card => (
                <div key={card.label} className="reporting-metric-row rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] p-4">
                  <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{card.label}</p>
                  <div className="mt-2 min-h-[2rem]">
                    {isLoadingReporting ? <CountSpinner /> : <p className="reporting-title text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{card.value}</p>}
                  </div>
                  <p className="reporting-muted mt-2 text-sm leading-6 text-[#667085]">{card.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="reporting-card rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.products.panel.eyebrow}</p>
                <h2 className="reporting-title mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.products.panel.title}</h2>
              </div>
              <span className="reporting-badge reporting-badge--purple rounded-full bg-[#f4f3ff] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7a5af8]">{copy.products.panel.badge}</span>
            </div>

            <div className="mt-5 space-y-4">
              {customerProductReporting.productSummary.map(card => (
                <div key={card.label} className="reporting-metric-row rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] p-4">
                  <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{card.label}</p>
                  <div className="mt-2 min-h-[2rem]">
                    {isLoadingReporting ? <CountSpinner /> : <p className="reporting-title text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{card.value}</p>}
                  </div>
                  <p className="reporting-muted mt-2 text-sm leading-6 text-[#667085]">{card.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="reporting-card rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <p className="reporting-eyebrow text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.notes.eyebrow}</p>
            <div className="reporting-muted mt-4 space-y-3 text-sm leading-6 text-[#667085]">
              {copy.notes.items.map(item => <p key={item}>{item}</p>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
